// Watch-an-LLM puzzle panel: stats of the last attempt plus the model
// switcher for the next one. Rendered by the puzzle-complete modal and by
// the pause menu so both show the same thing.
//
// puzzleLLM: { provider, model, stats, providers, keys,
//              onChangeModel(provider, model), onRetry }
// showStats: false hides the "this attempt" block (pause menu before any
//            attempt has been made).
import React from 'react';
import './PuzzleLLMPanel.css';

const fmt = (n) => (n == null ? '-' : Number(n).toLocaleString());

const PuzzleLLMPanel = ({ puzzleLLM, showStats = true }) => {
    if (!puzzleLLM) return null;
    const st = puzzleLLM.stats;
    const u = st?.usage || null;
    const cost = u?.costUsd != null
        ? (u.costUsd < 0.01 ? `$${u.costUsd.toFixed(4)}` : `$${u.costUsd.toFixed(3)}`)
        : '-';
    const secs = st?.thinkingTime != null ? `${st.thinkingTime.toFixed(1)}s` : '-';
    const current = `${puzzleLLM.provider}::${puzzleLLM.model}`;
    const hasKey = !!puzzleLLM.keys?.[puzzleLLM.provider];

    return (
        <div className="puzzle-llm">
            {showStats && (
                <>
                    <h4 className="stats-section-title">THIS ATTEMPT</h4>
                    {st ? (
                        <div className="puzzle-llm__stats">
                            <div className="puzzle-llm__row"><span>Model</span><strong>{st.model}</strong></div>
                            <div className="puzzle-llm__row"><span>Response time</span><strong>{secs}</strong></div>
                            <div className="puzzle-llm__row"><span>Tokens in / out</span><strong>{fmt(u?.input)} / {fmt(u?.output)}</strong></div>
                            {u?.reasoning > 0 && (
                                <div className="puzzle-llm__row"><span>of which reasoning</span><strong>{fmt(u.reasoning)}</strong></div>
                            )}
                            {u?.cached > 0 && (
                                <div className="puzzle-llm__row"><span>cached input</span><strong>{fmt(u.cached)}</strong></div>
                            )}
                            <div className="puzzle-llm__row"><span>Estimated cost</span><strong>{cost}</strong></div>
                            {st.attempts > 1 && (
                                <div className="puzzle-llm__row"><span>Parse retries</span><strong>{st.attempts - 1}</strong></div>
                            )}
                        </div>
                    ) : (
                        <div className="puzzle-llm__none">No model response recorded for this attempt.</div>
                    )}
                </>
            )}

            <h4 className="stats-section-title">NEXT ATTEMPT</h4>
            <label className="puzzle-llm__pick">
                <span>Model for Retry and New Puzzle</span>
                <select
                    className="puzzle-llm__select"
                    value={current}
                    onChange={(e) => {
                        const [pv, ...rest] = e.target.value.split('::');
                        puzzleLLM.onChangeModel?.(pv, rest.join('::'));
                    }}
                >
                    {Object.entries(puzzleLLM.providers || {}).map(([pv, models]) => {
                        const keyed = !!puzzleLLM.keys?.[pv];
                        return (
                            <optgroup key={pv} label={keyed ? pv : `${pv} (no API key stored)`}>
                                {models.map(m => (
                                    <option key={`${pv}::${m}`} value={`${pv}::${m}`} disabled={!keyed}>{m}</option>
                                ))}
                            </optgroup>
                        );
                    })}
                </select>
            </label>
            <div className="puzzle-llm__hint">
                {hasKey
                    ? `Using your stored ${puzzleLLM.provider} key. Retry replays this exact puzzle; New Puzzle draws a fresh one with the settings below.`
                    : `No ${puzzleLLM.provider} key is stored for this session. Add one from the main menu before retrying.`}
            </div>
        </div>
    );
};

export default PuzzleLLMPanel;
